import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  ContainerLoadTypeDto,
  CreateContainerLoadTypeDto,
  UpdateContainerLoadTypeDto,
} from '../../models/forwarding/container-load-type.model';

/**
 * @deprecated Use ContainerLoadTypeDto from the model file instead.
 * Kept here only for backward-compatibility with legacy modal components.
 */
export interface ContainerLoadType {
  id?: number;
  /** @deprecated mapped from name */
  label: string;
  code: string;
  /** @deprecated uppercase for legacy compat; new code uses lowercase 'sea'|'air'|'land' */
  category: 'SEA' | 'AIR';
  notes: string;
  createdAt: Date;
  updatedAt?: Date;
  isActive?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ContainerLoadTypesService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/forwarding/container-load-types`;

  /**
   * GET /api/forwarding/container-load-types[?category=sea&activeOnly=true]
   * Phase 1J: backend returns 7 rows (LCL/FCL20/FCL40/FCL40HC/FCL45/AIR/LAND).
   * Pass category and/or activeOnly to filter server-side.
   */
  getAll(filter?: { category?: 'sea' | 'air' | 'land'; activeOnly?: boolean }): Observable<ContainerLoadTypeDto[]> {
    let params = new HttpParams();
    if (filter?.category) {
      params = params.set('category', filter.category);
    }
    if (filter?.activeOnly) {
      params = params.set('activeOnly', 'true');
    }
    return this.http.get<ContainerLoadTypeDto[]>(this.apiUrl, { params });
  }

  getById(id: number): Observable<ContainerLoadTypeDto> {
    return this.http.get<ContainerLoadTypeDto>(`${this.apiUrl}/${id}`);
  }

  create(dto: CreateContainerLoadTypeDto): Observable<ContainerLoadTypeDto> {
    return this.http.post<ContainerLoadTypeDto>(this.apiUrl, dto);
  }

  update(id: number, dto: UpdateContainerLoadTypeDto): Observable<ContainerLoadTypeDto> {
    return this.http.put<ContainerLoadTypeDto>(`${this.apiUrl}/${id}`, dto);
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  // ---- Legacy API shims — used by forwarding-admin modal components ----
  // These methods are deprecated; new code should use the Observable methods above.

  /**
   * @deprecated Use getAll() with category filter.
   * This shim normalises for legacy components that expect ContainerLoadType shape.
   */
  getLoadTypes(): Observable<ContainerLoadType[]> {
    // legacy components only ever rendered SEA/AIR — map lowercase category to uppercase for compat
    return new Observable((obs) => {
      this.getAll().subscribe({
        next: (items) => {
          obs.next(
            items.map((lt) => ({
              id: lt.id,
              label: lt.name,
              code: lt.code,
              category: (lt.category === 'sea' ? 'SEA' : 'AIR') as 'SEA' | 'AIR',
              notes: '',
              isActive: lt.isActive,
              createdAt: new Date(0),
            } satisfies ContainerLoadType)),
          );
          obs.complete();
        },
        error: (err) => obs.error(err),
      });
    });
  }

  /** @deprecated Use delete() */
  deleteLoadType(id: number): void {
    this.delete(id).subscribe();
  }

  /** @deprecated Use update() */
  updateLoadType(id: number, data: Omit<ContainerLoadType, 'id' | 'createdAt'>): void {
    this.update(id, {
      code: data.code,
      name: data.label,
      category: (data.category === 'SEA' ? 'sea' : 'air') as 'sea' | 'air',
      isActive: data.isActive ?? true,
    }).subscribe();
  }

  /** @deprecated Use create() */
  addLoadType(data: Omit<ContainerLoadType, 'id' | 'createdAt'>): void {
    this.create({
      code: data.code,
      name: data.label,
      category: (data.category === 'SEA' ? 'sea' : 'air') as 'sea' | 'air',
    }).subscribe();
  }

  /** @deprecated No-op shim */
  refresh(): void {
    // no-op: service is stateless
  }
}
